import { useEffect, useRef, useState } from 'react'
import { cn } from '@/shared/lib/utils'

function toMs(target: Date | string | number): number {
  if (target instanceof Date) return target.getTime()
  if (typeof target === 'number') return target
  return new Date(target).getTime()
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

function formatRemaining(ms: number, dayUnit: string): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  if (days > 0) return `${days}${dayUnit} ${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`
  if (hours > 0) return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`
  return `${pad2(minutes)}:${pad2(seconds)}`
}

/**
 * Compte à rebours mono (drop « c'est mon tour », salle d'attente haute saison) - DESIGN §8.
 * Mise à jour numérique uniquement (compatible prefers-reduced-motion).
 * `ariaLive` par défaut `off` (pas d'annonce/seconde) ; régler selon le contexte.
 */
export function CountdownTimer({
  target,
  onComplete,
  ariaLive = 'off',
  size = 'md',
  dayUnit = 'j',
  className,
}: {
  target: Date | string | number
  onComplete?: () => void
  ariaLive?: 'off' | 'polite' | 'assertive'
  size?: 'md' | 'xxl'
  /** Suffixe i18n de l'unité « jour » (défaut FR `j`). */
  dayUnit?: string
  className?: string
}) {
  const targetMs = toMs(target)
  const [remaining, setRemaining] = useState(() => targetMs - Date.now())
  const completedRef = useRef(false)
  const onCompleteRef = useRef(onComplete)

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    completedRef.current = false
    const tick = () => {
      const next = targetMs - Date.now()
      setRemaining(next)
      if (next <= 0 && !completedRef.current) {
        completedRef.current = true
        onCompleteRef.current?.()
      }
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [targetMs])

  return (
    <time
      role="timer"
      aria-live={ariaLive}
      className={cn(
        'font-mono tabular-nums tracking-tight text-[var(--v2-ink)] [font-feature-settings:"tnum"]',
        size === 'xxl' ? 'text-[clamp(3rem,12vw,7rem)] leading-none' : 'text-2xl',
        className,
      )}
    >
      {formatRemaining(remaining, dayUnit)}
    </time>
  )
}
